/**
 * 변형 추적 및 처리 유틸리티
 * modify와 translate 시작/종료 시점에서 선택된 feature의 값들을 저장하고 처리합니다.
 */
export class TransformationTracker {
  constructor() {
    // 초기 상태 저장소 (imageId -> initialState)
    this.initialStates = new Map();

    // 현재 진행 중인 변형 정보
    this.currentTransformation = null;
  }

  /**
   * 변형 시작 시 초기 상태 저장
   * @param {string} operationType - 변형 타입 ('modify' | 'translate')
   * @param {Array} features - 선택된 feature들
   */
  saveInitialState(operationType, features) {
    console.log(`[TransformationTracker] ${operationType} 시작 - 초기 상태 저장`, {
      featureCount: features.length
    });

    // 기존 초기 상태 초기화
    this.initialStates.clear();

    // 각 feature의 초기 상태 저장
    features.forEach(feature => {
      const imageId = feature.get('imageId');
      if (!imageId) {
        console.warn('[TransformationTracker] imageId가 없는 feature 발견');
        return;
      }

      const geometry = feature.getGeometry();
      if (!geometry) {
        console.warn('[TransformationTracker] geometry가 없는 feature 발견', { imageId });
        return;
      }

      const coordinates = geometry.getCoordinates()[0];
      const center = this.calculateCenter(coordinates);

      const initialState = {
        imageId: imageId,
        coordinates: coordinates,
        center: center,
        timestamp: Date.now()
      };

      this.initialStates.set(imageId, initialState);

      console.log('[TransformationTracker] 초기 상태 저장 완료', {
        imageId: imageId,
        center: center,
        coordinatesCount: coordinates.length
      });
    });

    // 현재 변형 정보 설정
    this.currentTransformation = {
      type: operationType,
      startTime: Date.now(),
      featureCount: features.length
    };
  }

  /**
   * 변형 완료 시 최종 상태와 비교하여 변형 데이터 계산 (모든 처리 포함)
   * @param {Array} features - 변형된 feature들
   * @returns {Object} 변형 데이터
   */
  calculateFinalTransformation(features) {
    if (!this.currentTransformation) {
      console.warn('[TransformationTracker] 진행 중인 변형이 없습니다');
      return null;
    }

    console.log(`[TransformationTracker] ${this.currentTransformation.type} 완료 - 변형 계산 시작`);

    const transformations = [];

    features.forEach(feature => {
      const imageId = feature.get('imageId');
      if (!imageId) {
        console.warn('[TransformationTracker] imageId가 없는 feature 발견');
        return;
      }

      const initialState = this.initialStates.get(imageId);
      if (!initialState) {
        console.warn('[TransformationTracker] 초기 상태를 찾을 수 없음', { imageId });
        return;
      }

      const currentGeometry = feature.getGeometry();
      if (!currentGeometry) {
        console.warn('[TransformationTracker] 현재 geometry가 없는 feature 발견', { imageId });
        return;
      }

      const finalCoordinates = currentGeometry.getCoordinates()[0];
      const finalCenter = this.calculateCenter(finalCoordinates);

      // 모든 변형 계산 (완료 시점에서 처리)
      const transformation = this.calculateAllTransformations(
        initialState.coordinates,
        finalCoordinates,
        initialState.center,
        finalCenter
      );

      const transformationData = {
        imageId: imageId,
        operationType: this.currentTransformation.type,
        initialState: initialState,
        finalState: {
          imageId: imageId,
          coordinates: finalCoordinates,
          center: finalCenter
        },
        transformation: transformation,
        duration: Date.now() - this.currentTransformation.startTime
      };

      transformations.push(transformationData);

      console.log('[TransformationTracker] 변형 계산 완료', {
        imageId: imageId,
        transformation: transformation
      });
    });

    // 전체 변형 결과
    const result = {
      operationType: this.currentTransformation.type,
      timestamp: Date.now(),
      duration: Date.now() - this.currentTransformation.startTime,
      featureCount: features.length,
      transformations: transformations
    };

    // 상태 초기화
    this.clearCurrentTransformation();

    return result;
  }

  /**
   * 모든 변형을 한 번에 계산 (완료 시점에서 처리)
   * @param {Array} initialCoordinates - 초기 좌표
   * @param {Array} finalCoordinates - 최종 좌표
   * @param {Array} initialCenter - 초기 중심점
   * @param {Array} finalCenter - 최종 중심점
   * @returns {Object} 변형 데이터
   */
  calculateAllTransformations(initialCoordinates, finalCoordinates, initialCenter, finalCenter) {
    return {
      coordinates: finalCoordinates,
      center: finalCenter,
      rotationDelta: this.calculateRotationDelta(initialCoordinates, finalCoordinates),
      scaleDelta: this.calculateScaleDelta(initialCoordinates, finalCoordinates)
    };
  }

  /**
   * 중심점 변화 계산
   * @param {Array} initialCenter - 초기 중심점
   * @param {Array} finalCenter - 최종 중심점
   * @returns {Object} 중심점 변화량
   */
  calculateCenterDelta(initialCenter, finalCenter) {
    return {
      deltaX: finalCenter[0] - initialCenter[0],
      deltaY: finalCenter[1] - initialCenter[1],
      distance: Math.sqrt(
        Math.pow(finalCenter[0] - initialCenter[0], 2) +
        Math.pow(finalCenter[1] - initialCenter[1], 2)
      ),
      initialCenter: initialCenter,
      finalCenter: finalCenter
    };
  }

  /**
   * 회전각 변화 계산
   * @param {Array} initialCoords - 초기 좌표
   * @param {Array} finalCoords - 최종 좌표
   * @returns {Object} 회전각 변화량
   */
  calculateRotationDelta(initialCoords, finalCoords) {
    const initialRotation = this.calculateRotation(initialCoords);
    const finalRotation = this.calculateRotation(finalCoords);

    let deltaAngle = finalRotation - initialRotation;

    // 각도 정규화 (-π ~ π)
    while (deltaAngle > Math.PI) deltaAngle -= 2 * Math.PI;
    while (deltaAngle < -Math.PI) deltaAngle += 2 * Math.PI;

    return {
      deltaAngle: deltaAngle,
      deltaAngleDegrees: deltaAngle * 180 / Math.PI,
      initialRotation: initialRotation,
      finalRotation: finalRotation
    };
  }

  /**
   * 스케일 변화 계산
   * @param {Array} initialCoords - 초기 좌표
   * @param {Array} finalCoords - 최종 좌표
   * @returns {Object} 스케일 변화량
   */
  calculateScaleDelta(initialCoords, finalCoords) {
    const initialSize = this.calculatePolygonSize(initialCoords);
    const finalSize = this.calculatePolygonSize(finalCoords);

    return {
      deltaScaleRatio: Math.sqrt(
        (finalSize.width * finalSize.height) /
        (initialSize.width * initialSize.height)
      )
    };
  }


  /**
   * 중심점 계산
   * @param {Array} coordinates - 좌표 배열
   * @returns {Array} 중심점 [x, y]
   */
  calculateCenter(coordinates) {
    const xs = coordinates.map(coord => coord[0]);
    const ys = coordinates.map(coord => coord[1]);

    return [
      (Math.min(...xs) + Math.max(...xs)) / 2,
      (Math.min(...ys) + Math.max(...ys)) / 2
    ];
  }

  /**
   * 회전각 계산
   * @param {Array} coordinates - 좌표 배열
   * @returns {number} 회전각 (라디안)
   */
  calculateRotation(coordinates) {
    if (coordinates.length < 2) return 0;

    // 첫 번째 변의 벡터 계산
    const vector = [
      coordinates[1][0] - coordinates[0][0],
      coordinates[1][1] - coordinates[0][1]
    ];

    return Math.atan2(vector[1], vector[0]);
  }

  /**
   * 폴리곤 크기 계산
   * @param {Array} coordinates - 좌표 배열
   * @returns {Object} 크기 정보 {width, height}
   */
  calculatePolygonSize(coordinates) {
    const bounds = this.calculateBoundingBox(coordinates);

    return {
      width: bounds.maxX - bounds.minX,
      height: bounds.maxY - bounds.minY
    };
  }

  /**
   * 경계 상자 계산
   * @param {Array} coordinates - 좌표 배열
   * @returns {Object} 경계 상자 {minX, maxX, minY, maxY}
   */
  calculateBoundingBox(coordinates) {
    const xs = coordinates.map(coord => coord[0]);
    const ys = coordinates.map(coord => coord[1]);

    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys)
    };
  }

  /**
   * 현재 진행 중인 변형 정보 초기화
   */
  clearCurrentTransformation() {
    this.initialStates.clear();
    this.currentTransformation = null;
    console.log('[TransformationTracker] 변형 상태 초기화 완료');
  }

  /**
   * 진행 중인 변형이 있는지 확인
   * @returns {boolean}
   */
  hasActiveTransformation() {
    return this.currentTransformation !== null;
  }

  /**
   * 현재 변형 정보 가져오기
   * @returns {Object|null}
   */
  getCurrentTransformation() {
    return this.currentTransformation;
  }

  /**
   * 초기 상태 가져오기
   * @param {string} imageId
   * @returns {Object|null}
   */
  getInitialState(imageId) {
    return this.initialStates.get(imageId);
  }

  /**
   * 기존 좌표를 변형 데이터를 사용해서 새로운 좌표로 변환
   * @param {Array} originalCoordinates - 기존 좌표 배열
   * @param {Object} transformationData - 변형 데이터
   * @returns {Array} 변환된 좌표 배열
   */
  transformCoordinates(originalCoordinates, transformationData) {
    const { centerDelta, rotationDelta, scaleDelta } = transformationData.transformation;

    // 기존 중심점 계산
    const originalCenter = this.calculateCenter(originalCoordinates);

    // 각 좌표를 변환
    const transformedCoordinates = originalCoordinates.map(coord => {
      // 1. 중심점 기준으로 상대 좌표로 변환
      const relativeX = coord[0] - originalCenter[0];
      const relativeY = coord[1] - originalCenter[1];

      // 2. 스케일 적용
      const scaledX = relativeX * scaleDelta.deltaScaleRatio;
      const scaledY = relativeY * scaleDelta.deltaScaleRatio;

      // 3. 회전 적용 (라디안으로 변환)
      const rotationRad = rotationDelta.deltaAngleDegrees * (Math.PI / 180);
      const rotatedX = scaledX * Math.cos(rotationRad) - scaledY * Math.sin(rotationRad);
      const rotatedY = scaledX * Math.sin(rotationRad) + scaledY * Math.cos(rotationRad);

      // 4. 새로운 중심점으로 이동
      const newCenterX = originalCenter[0] + centerDelta.deltaX;
      const newCenterY = originalCenter[1] + centerDelta.deltaY;

      // 5. 최종 좌표 계산
      const finalX = rotatedX + newCenterX;
      const finalY = rotatedY + newCenterY;

      return [finalX, finalY];
    });

    console.log('[TransformationTracker] 좌표 변환 완료', {
      originalCoordinates: originalCoordinates.length,
      transformedCoordinates: transformedCoordinates.length,
      transformation: {
        centerDelta,
        rotationDelta,
        scaleDelta
      }
    });

    return transformedCoordinates;
  }

  /**
   * 서버 전송용 최적화된 변형 데이터 생성
   * @param {Array} features - 변형된 feature들
   * @returns {Array} 서버 전송용 변형 데이터 배열
   */
  createServerTransformationData(features) {
    const fullTransformation = this.calculateFinalTransformation(features);
    if (!fullTransformation) return null;

    // 서버 전송용으로 최적화된 데이터 생성
    const serverData = fullTransformation.transformations.map(transformation => {
      const finalCoordinates = transformation.finalState.coordinates;
      const finalCenter = transformation.finalState.center;
      const rotationDelta = transformation.transformation.rotationDelta.deltaAngle; // 라디안 단위
      const scaleDelta = transformation.transformation.scaleDelta.deltaScaleRatio;

      return {
        thermalImageId: transformation.imageId,
        positionCoordinates: finalCoordinates,
        centerCoordinate: finalCenter,
        scaleFactor: scaleDelta,
        rotation: rotationDelta // 이미 라디안 단위
      };
    });

    console.log('[TransformationTracker] 서버 전송용 데이터 생성', serverData);
    return serverData;
  }
}

// 싱글톤 인스턴스 생성
export const transformationTracker = new TransformationTracker();
